import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { ScrollText, ArrowRight } from "lucide-react";
import { fmtDateTime } from "../lib/datetime";

const ACTION_BADGE = {
  created: "badge-blue",
  edited: "badge-amber",
  status_changed: "badge-amber",
  deleted: "badge-red",
  countersigned: "badge-green",
  client_signed: "badge-green",
};

const TYPE_LABEL = {
  structure: "Structure",
  inspection: "Inspection",
  handover: "Handover",
  operative: "Operative",
  price_schedule: "Price ref",
  area: "Area",
  branding: "Branding",
};

export default function ActivityLog() {
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({ users: [], record_types: [] });
  const [filters, setFilters] = useState({ user_id: "", record_type: "", date_from: "", date_to: "" });

  useEffect(() => {
    api.get("/admin/activity-log/facets").then((r) => setFacets(r.data));
  }, []);

  const load = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/admin/activity-log", { params }).then((r) => setRows(r.data));
  };
  useEffect(load, [filters]);

  const setF = (k, v) => setFilters({ ...filters, [k]: v });

  return (
    <div data-testid="activity-log-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 18 }}>
        <div className="mono" style={{ color: "var(--brand-accent, #F05A14)", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>ADMIN · AUDIT TRAIL</div>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}><ScrollText size={26} style={{ verticalAlign: -3, marginRight: 8 }} />Activity Log</h1>
        <div style={{ color: "#64748b", marginTop: 4 }}>Append-only record of who changed what, when. Edits and status changes preserve the previous value — wrongly-dismantled structures stay traceable to who changed them.</div>
      </header>

      <div className="card" style={{ padding: 14, marginBottom: 12, display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }} data-testid="activity-filters">
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>User</label>
          <select data-testid="filter-user" className="input-base" value={filters.user_id} onChange={(e) => setF("user_id", e.target.value)}>
            <option value="">All users</option>
            {facets.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>Record type</label>
          <select data-testid="filter-record-type" className="input-base" value={filters.record_type} onChange={(e) => setF("record_type", e.target.value)}>
            <option value="">All record types</option>
            {facets.record_types.map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>From</label>
          <input data-testid="filter-from" className="input-base" type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} />
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: "#475569" }}>To</label>
          <input data-testid="filter-to" className="input-base" type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} />
        </div>
      </div>

      <div className="card" style={{ padding: 0 }} data-testid="activity-table">
        <table>
          <thead>
            <tr>
              <th>When</th><th>Who</th><th>Action</th><th>Record</th><th>Change</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`audit-row-${r.id}`}>
                <td className="mono" style={{ whiteSpace: "nowrap", color: "#475569" }}>{fmtDateTime(r.created_at)}</td>
                <td><span style={{ fontWeight: 600 }}>{r.user_name}</span><div className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>{r.user_email}</div></td>
                <td><span className={`badge ${ACTION_BADGE[r.action] || "badge-slate"}`}>{r.action.replace("_", " ")}</span></td>
                <td>{TYPE_LABEL[r.record_type] || r.record_type}<div className="mono" style={{ fontSize: 11, color: "#94a3b8" }}>{r.record_ref || r.record_id?.slice(0, 8)}</div></td>
                <td><ChangeCell detail={r.change_detail} /></td>
              </tr>
            ))}
            {rows.length === 0 ? <tr><td colSpan={5} style={{ textAlign: "center", padding: 24, color: "#94a3b8" }}>No matching activity</td></tr> : null}
          </tbody>
        </table>
      </div>

      <div className="mono" style={{ fontSize: 11, color: "#94a3b8", marginTop: 8 }}>{rows.length} entries shown. Append-only · entries cannot be edited or deleted.</div>
    </div>
  );
}

function ChangeCell({ detail }) {
  if (!detail) return <span style={{ color: "#94a3b8" }}>—</span>;
  if (Array.isArray(detail)) {
    return (
      <div style={{ display: "grid", gap: 4 }}>
        {detail.map((c, i) => (
          <div key={i} className="mono" style={{ fontSize: 12 }}>
            <b>{c.field}</b>:{" "}
            <span style={{ color: "#b91c1c", textDecoration: "line-through" }}>{stringify(c.old)}</span>
            {" "}<ArrowRight size={11} style={{ verticalAlign: -1 }} />{" "}
            <span style={{ color: "#065f46", fontWeight: 600 }}>{stringify(c.new)}</span>
          </div>
        ))}
      </div>
    );
  }
  if (typeof detail === "object") {
    return (
      <div className="mono" style={{ fontSize: 12, color: "#334155" }}>
        {Object.entries(detail).map(([k, v]) => <div key={k}><b>{k}</b>: {stringify(v)}</div>)}
      </div>
    );
  }
  return <span className="mono">{String(detail)}</span>;
}

function stringify(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
